/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, message, Button, Tooltip, Tag, Spin } from "antd";
import {
    DownloadOutlined,
    FilterOutlined,
    DownOutlined,
    UpOutlined,
    TeamOutlined,
    EnvironmentOutlined,
    BoxPlotOutlined,
    ThunderboltOutlined,
    InboxOutlined,
    RiseOutlined,
    FallOutlined,
    LineChartOutlined,
    MinusOutlined,
    ArrowLeftOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import "./MCNReport.scss";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadMCNReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { BOOKING_TYPE_OPTIONS, DOCUMENT_TYPE_OPTIONS, STATUS_OPTIONS } from "../../../../constants/reportConstants";
import Link from "antd/lib/typography/Link";
import { normalizeString } from "../../../../utils";
import { useNavigate } from "react-router-dom";
import back_img from "../../../../assests/images/rightVector.svg";

// ── Status → visual class mapper ─────────────────────────────────
const getStatusClass = (status = "") => {
    const s = status.toUpperCase();
    if (s === "DELIVERED" || s === "RTO_DELIVERED" || s === "RETURN_DELIVERED") return "status-delivered";
    if (s === "BOOKED" || s === "COMPLETED" || s === "UPDATED_BOOKING") return "status-booked";
    if (s.includes("INSCAN") || s.includes("INSCANNED") || s === "BAGGED") return "status-inscanned";
    if (s.includes("MANIFESTED") || s.includes("BAG_FINAL")) return "status-manifested";
    if (s.includes("RETURN") || s.includes("RTO") || s === "DAMAGED" || s === "DESTROYED" || s === "TERMINATED") return "status-return";
    if (s.includes("DELIVERY") || s.includes("OUT_SCAN") || s.includes("OUTSCAN") || s.includes("DRS")) return "status-pending";
    return "status-default";
};

// ── KPI card component ───────────────────────────────────────────
const KpiCard = ({ icon, label, value, sub, color, accent, colorDark, hero, split }) => (
    <div
        className={`kpi-card${hero ? " kpi-hero" : ""}`}
        style={{
            "--kpi-color": color,
            "--kpi-accent": accent,
            "--kpi-color-dark": colorDark || color,
        }}
    >
        <div className="kpi-top">
            <div className="kpi-icon">{icon}</div>
        </div>
        <div className="kpi-value">{typeof value === "number" ? value.toLocaleString() : (value ?? "—")}</div>
        <div className="kpi-label">{label}</div>
        {sub && <div className="kpi-sub">{sub}</div>}
        {split && (
            <div className="kpi-split">
                {split.map((s, i) => (
                    <div key={i} className="kpi-split-item">
                        <div className="kpi-split-dot" style={{ background: s.color }} />
                        <span>{s.label}</span>
                        <span className="kpi-split-val">{s.value.toLocaleString()}</span>
                    </div>
                ))}
            </div>
        )}
    </div>
);

// ── Ranked breakdown list ────────────────────────────────────────
const RankList = ({ items, labelKey, countKey, color, empty }) => {
    if (!items || items.length === 0) {
        return (
            <div className="analytics-empty">
                <InboxOutlined />
                <span>{empty || "No data available"}</span>
            </div>
        );
    }
    const max = items[0]?.[countKey] || 1;
    return (
        <div className="rank-list">
            {items.map((item, idx) => {
                const pct = Math.round((item[countKey] / max) * 100);
                const rankClass = idx === 0 ? "rank-1" : idx === 1 ? "rank-2" : idx === 2 ? "rank-3" : "";
                return (
                    <div className="rank-row" key={idx} style={{ "--rank-color": color }}>
                        <div className={`rank-num ${rankClass}`}>{idx + 1}</div>
                        <div className="rank-label" title={item[labelKey]}>{item[labelKey] || "—"}</div>
                        <div className="rank-bar-wrap">
                            <div className="rank-bar-track">
                                <div
                                    className="rank-bar-fill"
                                    style={{ 
                                        "--bar-w": `${item.percentage || 0}%`, 
                                        width: `${item.percentage || 0}%`,
                                        background: color 
                                    }}
                                />
                                <div 
                                    className="rank-bar-remaining"
                                    style={{ 
                                        width: `${100 - (item.percentage || 0)}%`,
                                        background: `${color}15` // Very light version of the same color
                                    }}
                                />
                            </div>
                            <div className="rank-count">
                                <span style={{ fontWeight: 700, color: "#111827" }}>{item[countKey]}</span>
                                {item.percentage !== undefined && (
                                    <span style={{ fontSize: 10, color: color, fontWeight: 600, marginLeft: 6 }}>
                                        {item.percentage.toFixed(1)}%
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ── Day-wise growth trend ────────────────────────────────────────
const DayWiseTrend = ({ analyticsData, dateRange, loading }) => {
    const dailyData = useMemo(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return [];

        // Build every date in the selected range
        const dates = [];
        const cur = dateRange[0].clone().startOf("day");
        const end = dateRange[1].clone().startOf("day");
        while (cur.isSameOrBefore(end, "day")) {
            dates.push(cur.format("YYYY-MM-DD"));
            cur.add(1, "day");
        }

        // Bucket records by date, split DOX vs NON-DOX
        const doxByDate = {};
        const nonDoxByDate = {};
        
        analyticsData.forEach((r) => {
            const timeStr = r.bookingCompleteTime;
            if (!timeStr) return;
            
            // Use moment to parse and format to standard YYYY-MM-DD for comparison
            // This handles BOTH DD-MM-YYYY (from sample) and YYYY-MM-DD formats
            const m = moment(timeStr, ["DD-MM-YYYY HH:mm:ss", "YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY", "YYYY-MM-DD"]);
            if (!m.isValid()) return;
            const d = m.format("YYYY-MM-DD");

            const docType = (r.doxNondox || "").toLowerCase();
            if (docType === "dox") {
                doxByDate[d] = (doxByDate[d] || 0) + 1;
            } else {
                // Count all other shipments (including untyped ones) as NON-DOX 
                // to ensure DOX + NON-DOX always equals the Daily Total
                nonDoxByDate[d] = (nonDoxByDate[d] || 0) + 1;
            }
        });

        return dates.map((date) => ({
            date,
            label: moment(date).format("DD MMM"),
            dayLabel: moment(date).format("ddd"),
            dox: doxByDate[date] || 0,
            nonDox: nonDoxByDate[date] || 0,
            total: (doxByDate[date] || 0) + (nonDoxByDate[date] || 0),
        }));
    }, [analyticsData, dateRange]);

    const maxTotal = useMemo(
        () => Math.max(...dailyData.map((d) => d.total), 1),
        [dailyData]
    );

    // Growth: compare second half of range vs first half
    const growthInfo = useMemo(() => {
        if (dailyData.length < 2) return null;
        const mid = Math.floor(dailyData.length / 2);
        const firstHalfSum = dailyData.slice(0, mid).reduce((s, d) => s + d.total, 0);
        const secondHalfSum = dailyData.slice(mid).reduce((s, d) => s + d.total, 0);
        if (firstHalfSum === 0 && secondHalfSum === 0) return null;
        if (firstHalfSum === 0) return { pct: 100, up: true, flat: false };
        const pct = Math.round(((secondHalfSum - firstHalfSum) / firstHalfSum) * 100);
        return { pct, up: pct > 0, flat: pct === 0 };
    }, [dailyData]);

    const peakDay = useMemo(
        () => dailyData.length ? dailyData.reduce((best, d) => (d.total > best.total ? d : best), dailyData[0]) : null,
        [dailyData]
    );

    if (!dailyData.length) return null;

    // For dense ranges show only the date (no day label)
    const showDayLabel = dailyData.length <= 14;

    return (
        <div className="trend-card">
            <div className="trend-header">
                <div className="trend-header-left">
                    <div className="trend-icon"><LineChartOutlined /></div>
                    <div>
                        <div className="trend-title">Day-wise Order Trend</div>
                        <div className="trend-sub">DOX vs NON-DOX split · {dailyData.length} days</div>
                    </div>
                </div>

                <div className="trend-header-right">
                    {growthInfo && (
                        <div className={`trend-growth-pill ${growthInfo.flat ? "flat" : growthInfo.up ? "up" : "down"}`}>
                            {growthInfo.flat ? <MinusOutlined /> : growthInfo.up ? <RiseOutlined /> : <FallOutlined />}
                            <span>
                                {growthInfo.flat
                                    ? "Stable"
                                    : `${Math.abs(growthInfo.pct)}% ${growthInfo.up ? "growth" : "decline"}`}
                            </span>
                            <span className="growth-context">(2nd half vs 1st half)</span>
                        </div>
                    )}
                    {peakDay && peakDay.total > 0 && (
                        <div className="trend-peak-pill">
                            Peak: <strong>{peakDay.label}</strong> · {peakDay.total} orders
                        </div>
                    )}
                    <div className="trend-legend">
                        <span className="legend-dot" style={{ background: "#2e7d32" }} />DOX
                        <span className="legend-dot" style={{ background: "#e65100", marginLeft: 10 }} />NON-DOX
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="trend-loading"><Spin size="small" /><span>Loading trend…</span></div>
            ) : (
                <div className="trend-chart-wrap">
                    {/* Y-axis grid lines */}
                    <div className="trend-y-grid">
                        {[100, 75, 50, 25, 0].map((pct) => (
                            <div key={pct} className="trend-grid-line" style={{ bottom: `${pct}%` }}>
                                <span className="trend-grid-label">
                                    {pct === 0 ? 0 : Math.round((maxTotal * pct) / 100)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="trend-bars">
                        {dailyData.map((d) => {
                            const totalBarPct = maxTotal > 0 ? (d.total / maxTotal) * 100 : 0;
                            const doxPct = d.total > 0 ? (d.dox / d.total) * 100 : 0;
                            const nonDoxPct = d.total > 0 ? (d.nonDox / d.total) * 100 : 0;
                            const isPeak = peakDay && d.date === peakDay.date && peakDay.total > 0;

                            return (
                                <Tooltip
                                    key={d.date}
                                    title={
                                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                                            <div style={{ fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.2)", marginBottom: 4, paddingBottom: 3 }}>
                                                {moment(d.date).format("ddd, DD MMM YYYY")}
                                            </div>
                                            <div>Total: <strong>{d.total}</strong></div>
                                            <div style={{ color: "#a5d6a7" }}>DOX: <strong>{d.dox}</strong></div>
                                            <div style={{ color: "#ffcc80" }}>NON-DOX: <strong>{d.nonDox}</strong></div>
                                        </div>
                                    }
                                    placement="top"
                                >
                                    <div className={`bar-col${isPeak ? " bar-col-peak" : ""}`}>
                                        {d.total > 0 && (
                                            <div className="bar-count">{d.total}</div>
                                        )}
                                        <div
                                            className="bar-stack"
                                            style={{ height: `${Math.max(totalBarPct, d.total > 0 ? 3 : 0)}%` }}
                                        >
                                            {/* NON-DOX on top (rendered first = visual top in column-reverse) */}
                                            <div className="bar-seg bar-nondox" style={{ flex: nonDoxPct }} />
                                            <div className="bar-seg bar-dox" style={{ flex: doxPct }} />
                                        </div>
                                        <div className="bar-label-wrap">
                                            {showDayLabel && <span className="bar-day">{d.dayLabel}</span>}
                                            <span className="bar-date">{d.label}</span>
                                        </div>
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main component ───────────────────────────────────────────────
const MCNReport = () => {
    const navigate = useNavigate();
    const tableRequest = useRequestCancellation();
    const analyticsRequest = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [isReady, setIsReady] = useState(false);

    const [dateRange, setDateRange] = useState([moment().subtract(7, "days"), moment()]);
    const [hubList, setHubList] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [selectedOriginHubs, setSelectedOriginHubs] = useState([]);
    const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
    const [selectedCurrentHubs, setSelectedCurrentHubs] = useState([]);
    const [selectedCurrentStatuses, setSelectedCurrentStatuses] = useState([]);
    const [selectedBookingType, setSelectedBookingType] = useState();
    const [selectedDoxNondox, setSelectedDoxNondox] = useState();

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    });
    const [reportData, setReportData] = useState([]);
    const [analyticsData, setAnalyticsData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastSynced, setLastSynced] = useState(null);

    // Active filter count (for badge on filter toggle)
    const activeFilterCount = useMemo(() => {
        let c = 0;
        if (selectedOriginHubs.length) c++;
        if (selectedDestinationHubs.length) c++;
        if (selectedCurrentHubs.length) c++;
        if (selectedCurrentStatuses.length) c++;
        if (selectedBookingType) c++;
        if (selectedDoxNondox) c++;
        return c;
    }, [selectedOriginHubs, selectedDestinationHubs, selectedCurrentHubs, selectedCurrentStatuses, selectedBookingType, selectedDoxNondox]);

    // Build API params (shared between table + analytics fetch)
    const buildParams = useCallback(() => {
        const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
        const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
        const resolvedOriginHubs = selectedOriginHubs?.includes("ALL")
            ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
            : selectedOriginHubs;
        const resolvedDestinationHubs = selectedDestinationHubs?.includes("ALL")
            ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
            : selectedDestinationHubs;
        const resolvedCurrentHubs = selectedCurrentHubs?.includes("ALL")
            ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
            : selectedCurrentHubs;

        return {
            fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
            toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
            originHubIds: resolvedOriginHubs.length > 0 ? resolvedOriginHubs.join(",") : undefined,
            destinationHubIds: resolvedDestinationHubs.length > 0 ? resolvedDestinationHubs.join(",") : undefined,
            currentHubIds: resolvedCurrentHubs.length > 0 ? resolvedCurrentHubs.join(",") : undefined,
            currentStatuses: selectedCurrentStatuses.length > 0 ? selectedCurrentStatuses.join(",") : undefined,
            bookingType: selectedBookingType || undefined,
            doxNondoxTypes: selectedDoxNondox || undefined,
        };
    }, [dateRange, selectedOriginHubs, selectedDestinationHubs, selectedCurrentHubs, selectedCurrentStatuses, selectedBookingType, selectedDoxNondox, headerHubList]);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for MCN Report", e);
            setHeaderHubList([]);
        }
    }, []);

    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            // Note: setActiveHubId is not used in this component as we no longer need to track it separately
            
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedOriginHubs([newActiveHubId]);
            } else {
                setSelectedOriginHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Fetch hub list and initialize on mount
    useEffect(() => {
        const init = async () => {
            try {
                const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const activeHub = JSON.parse(localStorage.getItem("active_hub") || "null");
                const activeHubId = activeHub?.hubId;

                const role = user?.roles?.[0];
                setHubUser(role);

                const hubs = await getHubList();
                setHubList(hubs);

                // Handle initial pre-selection
                if (activeHubId && activeHubId !== "ALL_HUBS") {
                    setSelectedOriginHubs([activeHubId]);
                } else if (role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                    setSelectedOriginHubs([]);
                } else {
                    const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
                    const userHubs = userFullData?.userInfo?.hubDetails || [];
                    if (userHubs?.length > 0) {
                        setSelectedOriginHubs(["ALL"]);
                    } else {
                        setSelectedOriginHubs([]);
                    }
                }

                setIsReady(true);
            } catch (err) {
                console.error("Error initialising MCN Report:", err);
                setIsReady(true);
            }
        };
        init();
    }, []);

    // Fetch paginated table data
    useEffect(() => {
        if (!isReady || !hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedOriginHubs.length === 0) return;
        const t = setTimeout(() => fetchTableData(), 300);
        return () => clearTimeout(t);
    }, [isReady, hubUser, dateRange, selectedOriginHubs, selectedDestinationHubs, selectedCurrentHubs, selectedCurrentStatuses, selectedBookingType, selectedDoxNondox, pagination.current, pagination.pageSize]);

    // Fetch analytics data (large page, separate from table)
    useEffect(() => {
        if (!isReady || !hubUser) return;
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedOriginHubs.length === 0) return;
        const t = setTimeout(() => fetchAnalyticsData(), 300);
        return () => clearTimeout(t);
    }, [isReady, hubUser, dateRange, selectedOriginHubs, selectedDestinationHubs, selectedCurrentHubs, selectedCurrentStatuses, selectedBookingType, selectedDoxNondox]);

    const fetchTableData = async () => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        const { signal, requestId } = tableRequest.createNewRequest("mcn-table");
        try {
            setLoading(true);
            setReportData([]);
            const response = await ReportsService.getMCNReport(
                { ...buildParams(), page: (pagination.current || 1) - 1, size: pagination.pageSize || 10 },
                { signal }
            );
            if (tableRequest.isLatestRequest(requestId)) {
                setReportData(response.data || []);
                setLastSynced(response?.lastRefreshedAt || null);
                setPagination((prev) => ({ ...prev, total: response.total || 0 }));
            }
        } catch (err) {
            if (err.name !== "AbortError") {
                console.error("Error fetching MCN table:", err);
                message.error("Failed to fetch MCN report data");
            }
        } finally {
            if (tableRequest.isLatestRequest(requestId)) setLoading(false);
        }
    };

    const fetchAnalyticsData = async () => {
        if (!dateRange?.[0] || !dateRange?.[1]) return;
        const { signal, requestId } = analyticsRequest.createNewRequest("mcn-analytics");
        try {
            setAnalyticsLoading(true);
            const response = await ReportsService.getMCNReport(
                { ...buildParams(), page: 0, size: 500 },
                { signal }
            );
            if (analyticsRequest.isLatestRequest(requestId)) {
                setAnalyticsData(response.data || []);
            }
        } catch (err) {
            if (err.name !== "AbortError") console.error("Analytics fetch error:", err);
        } finally {
            if (analyticsRequest.isLatestRequest(requestId)) setAnalyticsLoading(false);
        }
    };

    // Derive KPI + breakdown data from analyticsData
    const analytics = useMemo(() => {
        const d = analyticsData;
        if (!d.length) {
            return { doxCount: 0, nonDoxCount: 0, uniqueCPs: 0, uniquePincodes: 0, cpBreakdown: [], pincodeBreakdown: [] };
        }

        // DOX vs NON-DOX split
        const doxCount = d.filter((r) => r.doxNondox?.toLowerCase() === "dox").length;
        const nonDoxCount = d.filter((r) => r.doxNondox?.toLowerCase() !== "dox" && r.doxNondox).length;

        // Channel Partner breakdown
        const cpMap = {};
        d.forEach((r) => {
            const cp = r.originCpName || "Others";
            cpMap[cp] = (cpMap[cp] || 0) + 1;
        });
        const cpTotal = d.length;
        const cpBreakdown = Object.entries(cpMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([name, count]) => ({ 
                name, 
                count,
                percentage: (count / cpTotal) * 100
            }));

        // Destination pincode breakdown
        const pinMap = {};
        d.forEach((r) => {
            const pin = r.destinationPincode || "Unknown";
            pinMap[pin] = (pinMap[pin] || 0) + 1;
        });
        const pincodeBreakdown = Object.entries(pinMap)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([pincode, count]) => ({ 
                pincode, 
                count,
                percentage: (count / cpTotal) * 100
            }));

        const uniqueCPs = new Set(d.filter((r) => r.originCpName).map((r) => r.originCpName)).size;
        const uniquePincodes = new Set(d.filter((r) => r.destinationPincode).map((r) => r.destinationPincode)).size;

        return { doxCount, nonDoxCount, uniqueCPs, uniquePincodes, cpBreakdown, pincodeBreakdown };
    }, [analyticsData]);

    const handleDownload = async () => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
            message.warning("Please select a date range before downloading");
            return;
        }
        setDownloadLoading(true);
        try {
            await downloadMCNReport({
                ...buildParams(),
                originHubIds: selectedOriginHubs.length > 0 ? selectedOriginHubs : undefined,
                destinationHubIds: selectedDestinationHubs.length > 0 ? selectedDestinationHubs : undefined,
                currentHubIds: selectedCurrentHubs.length > 0 ? selectedCurrentHubs : undefined,
                currentStatuses: selectedCurrentStatuses.length > 0 ? selectedCurrentStatuses : undefined,
            });
            message.success("MCN report download initiated");
        } catch (err) {
            console.error("Download error:", err);
            message.error("Failed to download MCN report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination((prev) => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize,
        }));
    };

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleFilterChange = (setter) => (value) => {
        setter(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    // ── Table columns ─────────────────────────────────────────
    const columns = [
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 160,
            fixed: "left",
            align: "center",
            render: (text) => (
                <Link href={`/shipment-tracker?awb=${text}`} target="_blank" style={{ fontWeight: 600 }}>
                    {text}
                </Link>
            ),
        },
        {
            title: "Channel Partner",
            dataIndex: "originCpName",
            key: "originCpName",
            width: 160,
            align: "center",
            render: (text) =>
                text ? (
                    <Tag className="mcn-type-tag type-mcn" style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }} title={text}>
                        {text}
                    </Tag>
                ) : (
                    <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                ),
        },
        {
            title: "Origin Hub",
            dataIndex: "originHubName",
            key: "originHubName",
            width: 180,
            align: "center",
            render: (text) => text || "—",
        },
        {
            title: "Origin Pincode",
            dataIndex: "originPincode",
            key: "originPincode",
            width: 110,
            align: "center",
        },
        {
            title: "Dest. Pincode",
            dataIndex: "destinationPincode",
            key: "destinationPincode",
            width: 110,
            align: "center",
            render: (text) => (
                <span style={{ fontWeight: 600, color: "#7b1fa2" }}>{text || "—"}</span>
            ),
        },
        {
            title: "Dest. Hub",
            dataIndex: "destinationHubName",
            key: "destinationHubName",
            width: 180,
            align: "center",
        },
        {
            title: "Status",
            dataIndex: "currentStatus",
            key: "currentStatus",
            width: 150,
            align: "center",
            render: (text) => (
                <span className={`mcn-status-tag ${getStatusClass(text)}`}>
                    {text || "—"}
                </span>
            ),
        },
        {
            title: "Partner",
            dataIndex: "partnerCode",
            key: "partnerCode",
            width: 120,
            align: "center",
            render: (text) => text || <span style={{ color: "#9ca3af" }}>—</span>,
        },
        {
            title: "TPL AWB",
            dataIndex: "tplAwbNumber",
            key: "tplAwbNumber",
            width: 140,
            align: "center",
            render: (text, rec) =>
                rec?.partnerCode === "DELHIVERY" ? (
                    <span
                        style={{ color: "#1890ff", cursor: "pointer" }}
                        onClick={() => window.open(`https://www.delhivery.com/track-v2/package/${text}`, "_blank")}
                    >
                        {text ? normalizeString(text) : "—"}
                    </span>
                ) : (
                    <span>{text ? normalizeString(text) : "—"}</span>
                ),
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            width: 110,
            align: "center",
            render: (text) =>
                text ? (
                    <Tag color="default" style={{ fontSize: 11, fontWeight: 600 }}>
                        {text}
                    </Tag>
                ) : "—",
        },
        {
            title: "Doc Type",
            dataIndex: "doxNondox",
            key: "doxNondox",
            width: 120,
            align: "center",
            render: (text) =>
                text ? (
                    <span className={`mcn-type-tag ${text?.toLowerCase() === "dox" ? "type-dox" : "type-nondox"}`}>
                        {text?.toUpperCase()}
                    </span>
                ) : "—",
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            key: "serviceType",
            width: 120,
            align: "center",
        },
        {
            title: "Travel By",
            dataIndex: "travelBy",
            key: "travelBy",
            width: 90,
            align: "center",
            render: (text) =>
                text ? <Tag color={text?.toUpperCase() === "AIR" ? "blue" : "default"} style={{ fontSize: 11 }}>{text}</Tag> : "—",
        },
        {
            title: "Booked At",
            dataIndex: "bookingCompleteTime",
            key: "bookingCompleteTime",
            width: 165,
            align: "center",
            render: (text) => text ? <span style={{ fontSize: 12, color: "#6b7280" }}>{text}</span> : "—",
        },
        {
            title: "Current Hub",
            dataIndex: "currentHubName",
            key: "currentHubName",
            width: 180,
            align: "center",
        },
        {
            title: "Last Update",
            dataIndex: "currentOperationDatetime",
            key: "currentOperationDatetime",
            width: 165,
            align: "center",
            render: (text) => text ? <span style={{ fontSize: 12, color: "#6b7280" }}>{text}</span> : "—",
        },
        {
            title: "Receiver Address",
            dataIndex: "receiverAddress",
            key: "receiverAddress",
            width: 200,
            align: "center",
            render: (text) => text ? <Tooltip title={text}><span style={{ fontSize: 12 }}>{text.length > 40 ? `${text.slice(0, 40)}…` : text}</span></Tooltip> : "—",
        },
    ];

    const isDateValid = dateRange?.[0] && dateRange?.[1];

    return (
        <div className="mcn-dashboard">

            {/* ── Top bar ──────────────────────────────────────── */}
            <div className="mcn-topbar">
                <img
                    src={back_img}
                    className="pointer"
                    onClick={() => navigate(-1)}
                    style={{ marginRight: 12, width: 14 }}
                />
                <span className="mcn-topbar-title">MCN Report</span>
                <span className="mcn-topbar-badge">MCN</span>

                <div className="mcn-topbar-filters">
                    <div className="filter-group">
                        <div className="filter-group-label">Date Range</div>
                        <CalendarComponent
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                            size="small"
                        />
                    </div>

                    <div className="filter-group">
                        <div className="filter-group-label">Origin Hub</div>
                        <DropDownComponent
                            style={{ minWidth: 160 }}
                            placeholder="All Origin Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedOriginHubs, baseOptions);
                            }}
                            value={selectedOriginHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>

                    <div className="filter-group">
                        <div className="filter-group-label">Dest. Hub</div>
                        <DropDownComponent
                            style={{ minWidth: 160 }}
                            placeholder="All Dest. Hubs"
                            options={hubList}
                            onChange={handleFilterChange(setSelectedDestinationHubs)}
                            value={selectedDestinationHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                        />
                    </div>
                </div>

                <div className="mcn-topbar-meta">
                    {lastSynced && (
                        <span className="meta-sync">
                            Last synced: <span>{moment(lastSynced).format("DD MMM, HH:mm")}</span>
                        </span>
                    )}
                    <Tooltip title="Download MCN Report">
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={handleDownload}
                            loading={downloadLoading}
                            disabled={!isDateValid}
                            size="small"
                        >
                            Download
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {/* ── More filters panel ────────────────────────────── */}
            <div className="mcn-filters-panel">
                <div className="mcn-filters-toggle" onClick={() => setFiltersOpen((p) => !p)}>
                    <div className="filters-toggle-left">
                        <FilterOutlined />
                        <span>Advanced Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="filters-active-count">{activeFilterCount}</span>
                        )}
                    </div>
                    {filtersOpen ? <UpOutlined style={{ color: "#9ca3af" }} /> : <DownOutlined style={{ color: "#9ca3af" }} />}
                </div>

                {filtersOpen && (
                    <div className="mcn-filters-body">
                        <Row gutter={[12, 12]} style={{ marginTop: 14 }}>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div className="filter-item">
                                    <div className="filter-item-label">Current Hub</div>
                                    <DropDownComponent
                                        placeholder="All Current Hubs"
                                        options={hubList}
                                        onChange={handleFilterChange(setSelectedCurrentHubs)}
                                        value={selectedCurrentHubs}
                                        mode="multiple"
                                        allowClear
                                        maxTagCount="responsive"
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div className="filter-item">
                                    <div className="filter-item-label">Status</div>
                                    <DropDownComponent
                                        placeholder="All Statuses"
                                        options={STATUS_OPTIONS}
                                        onChange={handleFilterChange(setSelectedCurrentStatuses)}
                                        value={selectedCurrentStatuses}
                                        mode="multiple"
                                        allowClear
                                        maxTagCount="responsive"
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div className="filter-item">
                                    <div className="filter-item-label">Booking Type</div>
                                    <DropDownComponent
                                        placeholder="All Booking Types"
                                        options={BOOKING_TYPE_OPTIONS}
                                        onChange={handleFilterChange(setSelectedBookingType)}
                                        value={selectedBookingType}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div className="filter-item">
                                    <div className="filter-item-label">Document Type</div>
                                    <DropDownComponent
                                        placeholder="DOX / Non-DOX"
                                        options={DOCUMENT_TYPE_OPTIONS}
                                        onChange={handleFilterChange(setSelectedDoxNondox)}
                                        value={selectedDoxNondox}
                                        allowClear
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}
            </div>

            {/* ── KPI strip ─────────────────────────────────────── */}
            <div className="mcn-kpi-strip">
                <KpiCard
                    hero
                    icon={<BoxPlotOutlined />}
                    label="Total MCN Orders"
                    value={pagination.total}
                    sub={isDateValid ? `${dateRange[0].format("DD MMM")} → ${dateRange[1].format("DD MMM YYYY")}` : "Select date range"}
                    color="#1a73e8"
                    colorDark="#1557b0"
                    accent="#e8f0fe"
                    split={[
                        { label: "DOX", value: analytics.doxCount, color: "#34c759" },
                        { label: "NON-DOX", value: analytics.nonDoxCount, color: "#ff9500" },
                    ]}
                />
                <KpiCard
                    icon={<InboxOutlined />}
                    label="DOX Orders"
                    value={analyticsLoading ? "…" : analytics.doxCount}
                    sub="Document shipments"
                    color="#2e7d32"
                    accent="#e8f5e9"
                />
                <KpiCard
                    icon={<ThunderboltOutlined />}
                    label="NON-DOX Orders"
                    value={analyticsLoading ? "…" : analytics.nonDoxCount}
                    sub="Non-document shipments"
                    color="#e65100"
                    accent="#fff3e0"
                />
                <KpiCard
                    icon={<TeamOutlined />}
                    label="Active Channel Partners"
                    value={analyticsLoading ? "…" : analytics.uniqueCPs}
                    sub="Unique CPs booking orders"
                    color="#7b1fa2"
                    accent="#f3e5f5"
                />
                <KpiCard
                    icon={<EnvironmentOutlined />}
                    label="Dest. Pincodes Covered"
                    value={analyticsLoading ? "…" : analytics.uniquePincodes}
                    sub="Unique delivery pincodes"
                    color="#00796b"
                    accent="#e0f2f1"
                />
            </div>

            {/* ── Day-wise trend ────────────────────────────────── */}
            <DayWiseTrend
                analyticsData={analyticsData}
                dateRange={dateRange}
                loading={analyticsLoading}
            />

            {/* ── Analytics (CP + Pincode breakdown) ────────────── */}
            <div className="mcn-analytics-row">
                <div className="analytics-card">
                    <div className="analytics-header">
                        <div className="analytics-icon" style={{ background: "#e8f0fe", color: "#1a73e8" }}>
                            <TeamOutlined />
                        </div>
                        <div className="analytics-title">Top Channel Partners by Order Volume</div>
                        <div className="analytics-badge">Top 8</div>
                    </div>
                    <Spin spinning={analyticsLoading}>
                        <RankList
                            items={analytics.cpBreakdown}
                            labelKey="name"
                            countKey="count"
                            color="#7b1fa2"
                            empty="No channel partner data found"
                        />
                    </Spin>
                </div>

                <div className="analytics-card">
                    <div className="analytics-header">
                        <div className="analytics-icon" style={{ background: "#e0f2f1", color: "#00796b" }}>
                            <EnvironmentOutlined />
                        </div>
                        <div className="analytics-title">Top Destination Pincodes</div>
                        <div className="analytics-badge">Top 8</div>
                    </div>
                    <Spin spinning={analyticsLoading}>
                        <RankList
                            items={analytics.pincodeBreakdown}
                            labelKey="pincode"
                            countKey="count"
                            color="#00796b"
                            empty="No pincode data found"
                        />
                    </Spin>
                </div>
            </div>

            {/* ── Detail table ──────────────────────────────────── */}
            <div className="mcn-table-section">
                <div className="mcn-table-header">
                    <div className="mcn-table-title">Shipment Details</div>
                    <div className="mcn-table-meta">
                        Showing <span>{pagination.total.toLocaleString()}</span> shipments
                        {analyticsData.length > 0 && analyticsData.length < pagination.total && (
                            <> · Analytics based on top <span>{analyticsData.length}</span> records</>
                        )}
                    </div>
                </div>
                <TableComponent
                    columns={columns}
                    dataSource={reportData}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ x: 2400 }}
                    rowKey="awbNumber"
                />
            </div>
        </div>
    );
};

export default MCNReport;
